import React from "react";
import { useTranslation } from "react-i18next";

// Firebase
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp,
  deleteDoc,
  doc,
  getDocs,
  writeBatch
} from "firebase/firestore";

export default function Hydra() {
  const { t } = useTranslation();

  const [inputChests, setInputChests] = React.useState("");
  const [history, setHistory] = React.useState([]);
  const [runs, setRuns] = React.useState(0);
  const [chests, setChests] = React.useState(0);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  // ------------------------------------------------
  // 🔥 Load from Firestore + cleanup rules
  // ------------------------------------------------
  React.useEffect(() => {
    const q = query(collection(db, "hydraRuns"), orderBy("timestamp", "desc"));

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => {
        const entry = d.data();
        const timestamp = entry.timestamp.toDate();
        return {
          id: d.id,
          chests: entry.chests,
          timestamp,
          date: timestamp.toLocaleString()
        };
      });

      // 🔥 Delete older than 2 days
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 2);
      cutoff.setHours(0, 0, 0, 0);

      data.forEach((item) => {
        if (item.timestamp < cutoff) {
          deleteDoc(doc(db, "hydraRuns", item.id));
        }
      });

      // 🔥 Keep only last 50 runs
      if (data.length > 50) {
        const toDelete = data.slice(50);
        toDelete.forEach((item) => deleteDoc(doc(db, "hydraRuns", item.id)));
      }

      const cleaned = data.slice(0, 50);

      setHistory(cleaned);
      setRuns(cleaned.length);
      setChests(cleaned.reduce((a, v) => a + v.chests, 0));
    });

    return () => unsub();
  }, []);

  // ------------------------------------------------
  // 🔥 Add run
  // ------------------------------------------------
  const handleAdd = () => {
    if (inputChests === "") return;

    let c = Number(inputChests);
    if (isNaN(c)) c = 0;
    if (c < 0) c = 0;
    if (c > 5) c = 5;

    if (c === 0) {
      setShowConfirmModal(true);
      return;
    }

    addRun(c);
  };

  const addRun = async (c) => {
    await addDoc(collection(db, "hydraRuns"), {
      chests: c,
      timestamp: Timestamp.now()
    });

    setInputChests("");
  };

  const confirmZero = () => {
    addRun(0);
    setShowConfirmModal(false);
  };

  const cancelZero = () => setShowConfirmModal(false);

  // ------------------------------------------------
  // 🔥 Delete run
  // ------------------------------------------------
  const deleteRun = async (id) => {
    if (!window.confirm(t("hydra.deleteConfirm"))) return;
    await deleteDoc(doc(db, "hydraRuns", id));
  };

  // ------------------------------------------------
  // 🔥 Reset total (batch delete)
  // ------------------------------------------------
  const resetAll = async () => {
    const ok = window.confirm(t("hydra.resetConfirm"));
    if (!ok) return;

    try {
      const ref = collection(db, "hydraRuns");
      const docs = await getDocs(ref);

      const batch = writeBatch(db);

      docs.forEach((d) => batch.delete(d.ref));

      await batch.commit();

      setHistory([]);
      setRuns(0);
      setChests(0);
      setInputChests("");
    } catch (err) {
      console.error("Reset failed:", err);
    }
  };

  // ------------------------------------------------
  // Calcul
  // ------------------------------------------------
  const average = runs > 0 ? (chests / runs).toFixed(2) : "0.00";

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-purple-300">
            {t("hydra.title")}
          </h2>
          <p className="text-gray-400 text-sm mt-1">{t("hydra.subtitle")}</p>
        </div>

        <button
          onClick={resetAll}
          className="bg-red-600 hover:bg-red-700 transition px-4 py-2 rounded-lg font-semibold shadow-md text-sm"
        >
          {t("hydra.reset")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label={t("hydra.totalRuns")} value={runs} />
        <StatCard label={t("hydra.totalChests")} value={chests} />
        <StatCard label={t("hydra.average")} value={average} />
      </div>

      {/* INPUT */}
      <div className="bg-[#1e1e26] border border-gray-800 rounded-2xl shadow-lg p-6 mb-8 max-w-xl">
        <label className="block text-gray-300 text-sm mb-1">
          {t("hydra.inputLabel")}
        </label>

        <input
          type="number"
          min="0"
          max="5"
          value={inputChests}
          onFocus={() => setInputChests("")}
          onChange={(e) => {
            let v = e.target.value;

            if (v === "") {
              setInputChests("");
              return;
            }

            v = Number(v);
            if (isNaN(v)) v = 0;
            if (v < 0) v = 0;
            if (v > 5) v = 5;

            setInputChests(v);
          }}
          className="w-full p-3 rounded-lg bg-[#2c2c36] text-white mt-1 focus:ring-2 focus:ring-blue-500 outline-none border border-gray-700 text-sm"
        />

        <button
          onClick={handleAdd}
          className="w-full mt-4 bg-blue-600 hover:bg-blue-700 transition p-3 rounded-lg font-semibold text-sm"
        >
          {t("hydra.addButton")}
        </button>
      </div>

      {/* HISTORY */}
      <section className="bg-[#1e1e26] border border-gray-800 rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">{t("hydra.history")}</h3>
          <span className="text-xs text-gray-500">{history.length} {t("hydra.runsSuffix")}</span>
        </div>

        {history.length === 0 ? (
          <p className="text-gray-500 text-sm">{t("hydra.noHistory")}</p>
        ) : (
          <div className="divide-y divide-gray-800">
            {history.map((entry) => (
              <div
                key={entry.id}
                className="py-2 flex items-center justify-between text-sm"
              >
                <div>
                  <span className="block text-gray-300">{entry.date}</span>
                  <span className="font-semibold text-purple-300">
                    {entry.chests} {t("hydra.chestsSuffix")}
                  </span>
                </div>

                <button
                  onClick={() => deleteRun(entry.id)}
                  className="text-red-500 hover:text-red-400 font-bold text-lg px-3"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ZERO CONFIRM */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#22222a] p-6 rounded-xl shadow-2xl w-80 text-center border border-gray-700">
            <h2 className="text-xl font-bold mb-4">
              {t("hydra.confirmZeroTitle")}
            </h2>
            <p className="mb-6 text-gray-300 text-sm">
              {t("hydra.confirmZeroText")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelZero}
                className="flex-1 bg-gray-600 hover:bg-gray-700 p-2 rounded-lg transition text-sm"
              >
                {t("hydra.cancel")}
              </button>
              <button
                onClick={confirmZero}
                className="flex-1 bg-blue-600 hover:bg-blue-700 p-2 rounded-lg transition font-semibold text-sm"
              >
                {t("hydra.continue")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-[#1e1e26] border border-gray-800 rounded-2xl p-4 flex flex-col gap-1 shadow-md">
      <span className="text-xs uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <span className="text-2xl font-semibold">{value}</span>
    </div>
  );
}
